// src/utils/api.js
import { useAuthContext } from '@asgardeo/auth-react';
import { PAYMENTS_SERVER_URL } from '../config/backend-endpoints';

export const useAuthenticatedApi = () => {
  const { getAccessToken, getBasicUserInfo } = useAuthContext();

  const makeAuthenticatedRequest = async (url, options = {}) => {
    try {
      // Get access token
      const accessToken = await getAccessToken();
      
      if (!accessToken) {
        throw new Error('No access token available');
      }

      // Prepare headers with authentication
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        ...options.headers,
      };

      // Make the request
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        throw new Error('Authentication failed - please login again');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  };

  const getUserInfo = async () => {
    return await getBasicUserInfo();
  };

  return {
    makeAuthenticatedRequest,
    getUserInfo,
  };
};

// Helper functions for common API calls
export const subscriptionApi = {
  get: async (email, makeAuthenticatedRequest) => {
    return await makeAuthenticatedRequest(
      `${import.meta.env.VITE_API_DOMAIN_URL}/subscription/${email}`
    );
  },

  create: async (data, makeAuthenticatedRequest) => {
    return await makeAuthenticatedRequest(
      `${import.meta.env.VITE_API_DOMAIN_URL}/subscription`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },

  update: async (email, data, makeAuthenticatedRequest) => {
    return await makeAuthenticatedRequest(
      `${import.meta.env.VITE_API_DOMAIN_URL}/subscription/${email}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  },
};

export const paymentApi = {
  start: async (data, makeAuthenticatedRequest) => {
    return await makeAuthenticatedRequest(
      `${PAYMENTS_SERVER_URL}/payment/start`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  },
};

// Firm API for preapproval flow (public endpoints - no auth required)
export const firmApi = {
  get: async (firmSlug) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_DOMAIN_URL}/firms/${firmSlug}`
    );
    if (!response.ok) {
      if (response.status === 404) {
        return { success: false, error: 'Firm not found' };
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.json();
  },
};

// Preapproval API for tokenized payments (public endpoint - no auth required)
export const preapprovalApi = {
  start: async (data) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_DOMAIN_URL}/payment/preapproval/start`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.json();
  },
};
